import { Row, Table } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';

import { ParsedCustomFieldConfig } from '@work4all/data/lib/custom-fields';
import {
  IResponse,
  useDataProvider,
} from '@work4all/data/lib/hooks/data-provider';

import { DataRequest } from '@work4all/models/lib/DataProvider';

import { TableRow } from '../../types';

import { DataTableColumnConfigLike } from './hooks/useFetchGroupByV8';
import { useGroupByDataV8 } from './hooks/useGroupByDataV8';
import { GroupedItems } from './types';

export const useQueryTableDataV8 = <T>(
  table: Table<T>,
  columns: DataTableColumnConfigLike[],
  requestedData: DataRequest,
  getGroupKeyFields: (field: string) => {
    id: string;
    label: string;
  },
  pageSize = 100,
  customFields,
  dataColumns: string[] = [],
  enableGroupsFooter = false
): {
  data: GroupedItems;
  total: number | 'rowsLength';
  isItemLoaded?: (index: number, row: TableRow) => boolean;
  fetchMore: (
    startIndex: number,
    stopIndex: number,
    rows: Row<T>[]
  ) => Promise<void>;
  refetch: () => Promise<void>;
  loading?: boolean;
  pending?: boolean;
  customFields?: ParsedCustomFieldConfig[];
} => {
  const tableState = table.getState();
  /** run default useDataProvider in case there's no groupings  */
  const withGroupings = tableState.grouping.length > 0;

  const defaultData = usePatchedDefaultDataV8(
    useDataProvider(requestedData, withGroupings, pageSize),
    pageSize
  );

  const groupByData = useGroupByDataV8(
    table,
    columns,
    requestedData,
    pageSize,
    getGroupKeyFields,
    dataColumns,
    !withGroupings,
    customFields,
    enableGroupsFooter
  );

  return withGroupings ? groupByData : defaultData;
};

export function usePatchedDefaultDataV8<T>(
  response: IResponse<T>,
  pageSize: number
) {
  const fetchMore = useCallback(
    async (startIndex: number) => {
      await response.fetchMore(startIndex, startIndex + pageSize - 1);
    },
    [pageSize, response]
  );

  return useMemo(() => {
    if (response.data.length >= response.total) {
      return { ...response, fetchMore };
    }

    const missingRows = response.total - response.data.length;
    const skeletonRowsCount = Math.min(3, missingRows);
    const skeletonRows = Array(skeletonRowsCount).fill(null);

    return {
      ...response,
      data: [...response.data, ...skeletonRows],
      fetchMore,
    };
  }, [response, fetchMore]);
}
